import { useStore } from "@nanostores/preact";
import { useEffect, useState } from "preact/hooks";
import {
  addCollaborator, ApiError, discardDraft, getMe, getParcels, getPlot, getPlots, movePlot,
  type ParcelInfo, type PlotDetail, removeCollaborator,
} from "../net/api";
import { $compass, $compassLoaded, $compassSaving, MAX_MARKS } from "../state/compass";
import { $myPlots, $panel, $plots, $session, toast } from "../state/stores";
import { townCells } from "../world/townMap";
import { AddMarkForm } from "./AddMarkForm";
import { describeError } from "./errors";
import { Panel } from "./Panel";
import { TownMap } from "./TownMap";

const errText = (e: unknown) => (e instanceof ApiError ? describeError(e.code) : "無法連線到伺服器");

/** Roles and plot lists change after moves and collaborator edits; re-read both. */
async function afterChange(): Promise<void> {
  $session.set(await getMe());
  $plots.set(await getPlots());
}

export function PlotPanel() {
  const mine = useStore($myPlots);
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <Panel title="領地" onClose={() => $panel.set(null)}>
      {selected ? (
        <>
          <PlotDetailView id={selected} />
          <button class="secondary" onClick={() => setSelected(null)}>返回</button>
        </>
      ) : (
        <>
          {mine.length > 0 && (
            <>
              <h3>我的領地</h3>
              {mine.map((p) => (
                <button key={p.id} class="secondary" onClick={() => setSelected(p.id)}>
                  {p.name}（{p.role === "owner" ? "主人" : "協作者"}）
                </button>
              ))}
            </>
          )}
        </>
      )}
    </Panel>
  );
}

function MoveSection(props: { plotId: string; parcelId: string | undefined; onMoved(): void }) {
  const [parcels, setParcels] = useState<ParcelInfo[] | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const compassLoaded = useStore($compassLoaded);
  const compassSaving = useStore($compassSaving);
  const loaded = compassLoaded && !compassSaving;
  const full = useStore($compass).marks.length >= MAX_MARKS;
  const cells = parcels ? townCells(parcels, props.parcelId ?? null) : [];
  const picked = cells.find((c) => c.id === target);
  const pickedParcel = parcels?.find((p) => p.id === target);
  useEffect(() => { getParcels().then(setParcels).catch((e) => setError(errText(e))); }, [props.parcelId]);

  async function move() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await movePlot(props.plotId, target);
      await afterChange();
      toast(`已搬到 ${target}`);
      setTarget(null);
      props.onMoved();
    } catch (e) {
      setError(errText(e));
      getParcels().then(setParcels).catch(() => { /* keep the old map */ });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3>地塊 {props.parcelId ?? ""} · 搬家</h3>
      <p class="hint">點一塊地：空地（淺色）可以搬過去，擺設與協作者會一起搬；任何一塊地都能加入羅盤。</p>
      {parcels
        ? <TownMap cells={cells} selected={target} onPick={(id) => { setTarget(id); setMarking(false); }} />
        : !error && <p class="hint">載入中…</p>}
      {picked && pickedParcel && (marking
        ? <AddMarkForm defaultName={picked.id}
            pos={[(pickedParcel.bounds.min[0] + pickedParcel.bounds.max[0]) / 2, (pickedParcel.bounds.min[1] + pickedParcel.bounds.max[1]) / 2]}
            onDone={() => setMarking(false)} />
        : (
          <>
            <p>{picked.state === "free" ? `搬到 ${picked.id}？` : `地塊 ${picked.id}`}</p>
            {picked.state === "free" && <button disabled={busy} onClick={() => void move()}>確定搬家</button>}
            <button class="secondary" disabled={!loaded || full} onClick={() => setMarking(true)}>加入羅盤</button>
            <button class="secondary" onClick={() => setTarget(null)}>取消</button>
          </>
        ))}
      {error && <div class="error">{error}</div>}
    </>
  );
}

function PlotDetailView(props: { id: string }) {
  const [d, setD] = useState<PlotDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState("");
  const load = () => getPlot(props.id).then(setD).catch((e) => setError(errText(e)));
  useEffect(() => { void load(); }, [props.id]);
  if (!d) return error ? <div class="error">{error}</div> : <p class="hint">載入中…</p>;
  const owner = d.my_role === "owner";

  async function run(fn: () => Promise<unknown>, done: string, roles = false) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast(done);
      if (roles) await afterChange();
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p>{d.name} · {d.kind === "estate" ? `主人 ${d.owner_name ?? ""}` : "公共展示區"}</p>
      <p class="hint">
        範圍 ({d.bounds.min.join(", ")}) – ({d.bounds.max.join(", ")}) · 已擺放 {d.used}/{d.capacity} · live 版本 {d.live_version} ·{" "}
        {d.draft_version ? `draft 版本 ${d.draft_version}` : "沒有 draft"}
      </p>
      {d.kind === "estate" && (
        <>
          <button class="secondary" disabled={busy || !d.draft_version} onClick={() => void run(() => discardDraft(props.id), "draft 已捨棄")}>捨棄 draft</button>
          <h3>協作者</h3>
          {d.collaborators.length === 0 && <p class="hint">還沒有協作者。</p>}
          <table>
            <tbody>
              {d.collaborators.map((c) => (
                <tr key={c.id}>
                  <td>{c.display_name}</td>
                  <td>{c.role === "builder" ? "建造者" : c.role}</td>
                  <td>{owner && <button class="secondary" disabled={busy} onClick={() => void run(() => removeCollaborator(props.id, c.id), "已移除協作者")}>移除</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {owner && (
            <form onSubmit={(e) => { e.preventDefault(); void run(() => addCollaborator(props.id, account.trim()), "已新增協作者").then(() => setAccount("")); }}>
              <label>新增協作者（email 或帳號 id）</label>
              <input type="text" required value={account} onInput={(e) => setAccount((e.target as HTMLInputElement).value)} />
              <button type="submit" disabled={busy}>新增</button>
            </form>
          )}
          {owner && <MoveSection plotId={props.id} parcelId={d.parcel_id} onMoved={() => void load()} />}
        </>
      )}
      {error && <div class="error">{error}</div>}
    </>
  );
}
